import React, { useState } from 'react';
import { Tag, Button, Typography, Space, Tooltip, message, Modal, Table } from 'antd';
import {
  CheckCircleOutlined, ClockCircleOutlined, LoadingOutlined, CloseCircleOutlined,
  FileWordOutlined, FileExcelOutlined, FilePdfOutlined, DownloadOutlined,
  FileOutlined, CheckCircleFilled,
  EyeOutlined, ThunderboltOutlined,
  BulbOutlined, DownOutlined, UpOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CollabTask, getFileDownloadUrl, getFilePreview, FilePreview, TaskProgress, generateWordFromTask } from '../../services/teamCollabApi';
import { useTeamCollabStore } from '../../stores/teamCollabStore';

const { Text } = Typography;

interface TaskCardProps {
  task: CollabTask;
  currentUserId?: string;
  onTryIt?: (task: CollabTask) => void;
}

const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  pending: { color: 'default', icon: <ClockCircleOutlined />, label: '等待中' },
  running: { color: 'processing', icon: <LoadingOutlined spin />, label: '执行中' },
  completed: { color: 'success', icon: <CheckCircleOutlined />, label: '已完成' },
  failed: { color: 'error', icon: <CloseCircleOutlined />, label: '失败' },
};

function trimCommand(cmd: string): string {
  // 去掉后端加的 "## 用户指令:\n" 前缀，显示完整内容
  const m = cmd.match(/## 用户指令:\s*\n([\s\S]*?)$/);
  if (m) return m[1].trim();
  return cmd;
}

/** 简单Markdown转HTML：支持标题/列表/粗体/代码/表格 */
function renderMarkdown(text: string): string {
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  
  // 代码块 ```...```
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, '<pre style="background:#1e1e2e;color:#cdd6f4;padding:10px 14px;border-radius:8px;overflow-x:auto;font-size:13px;line-height:1.5;margin:8px 0;"><code>$2</code></pre>');
  // 行内代码 `...`
  html = html.replace(/`([^`]+)`/g, '<code style="background:rgba(0,0,0,0.06);padding:2px 6px;border-radius:4px;font-size:13px;">$1</code>');
  // 粗体 **...**
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  // ### 三级标题
  html = html.replace(/^### (.+)$/gm, '<h4 style="font-size:15px;font-weight:700;color:#1d3b73;margin:10px 0 4px;border-left:3px solid #1677ff;padding-left:10px;">$1</h4>');
  // ## 二级标题
  html = html.replace(/^## (.+)$/gm, '<h3 style="font-size:16px;font-weight:700;color:#0a2540;margin:12px 0 6px;border-bottom:1px solid #e8e8e8;padding-bottom:4px;">$1</h3>');
  // # 一级标题
  html = html.replace(/^# (.+)$/gm, '<h2 style="font-size:18px;font-weight:800;color:#0a2540;margin:14px 0 8px;">$1</h2>');
  // 无序列表
  html = html.replace(/^- (.+)$/gm, '<li style="margin-left:20px;line-height:1.8;">$1</li>');
  // 连续li裹在ul里
  html = html.replace(/(<li[^>]*>[\s\S]*?<\/li>)/g, (m) => `<ul style="margin:4px 0;padding:0;">${m}</ul>`);
  // 有序列表
  html = html.replace(/^\d+\.\s+(.+)$/gm, '<li style="margin-left:20px;line-height:1.8;">$1</li>');
  // 水平线
  html = html.replace(/^---$/gm, '<hr style="border:none;border-top:1px solid #e8e8e8;margin:12px 0;"/>');
  // 换行
  html = html.replace(/\n/g, '<br/>');
  // ▶ 或 ✅ 开头做highlight
  html = html.replace(/^(▶|✅|📊|📄|⚠️|❌|👉)(.+)$/gm, '<div style="font-weight:600;color:#1677ff;margin:2px 0;">$1$2</div>');

  return html;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, currentUserId, onTryIt }) => {
  const [expanded, setExpanded] = useState(false);
  const [thinkingExpanded, setThinkingExpanded] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewData, setPreviewData] = useState<FilePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [wordGenerating, setWordGenerating] = useState(false);
  const cfg = statusConfig[task.status] || statusConfig.pending;
  const isFileResult = task.resultType === 'word' || task.resultType === 'excel' || task.resultType === 'pdf';
  const progressSteps: TaskProgress[] = useTeamCollabStore((s) => s.progressMap[task.id]) || [];
  const thinkingContent = (task as any).thinkingContent as string | undefined;
  const hasThinking = !!thinkingContent;
  const isOwn = !!currentUserId && task.userId === currentUserId;

  // 思考进行中自动展开面板，让用户看到实时思考内容
  React.useEffect(() => {
    if (task.status === 'running' && hasThinking) {
      setThinkingExpanded(true);
    }
  }, [task.status, hasThinking]);

  const handlePreview = async () => {
    if (!task.resultFileId) return;
    setPreviewVisible(true); setPreviewLoading(true);
    try { setPreviewData(await getFilePreview(task.resultFileId)); } catch (e: any) { setPreviewData({ type: 'text', error: e.message }); } finally { setPreviewLoading(false); }
  };

  const handleDownload = async () => {
    if (!task.resultFileId) return;
    try {
      let token = '';
      try { const s = JSON.parse(localStorage.getItem('auth-storage') || '{}'); token = s?.state?.token || ''; } catch {}
      const res = await fetch(getFileDownloadUrl(task.resultFileId), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = task.resultFileName || task.resultFileId; a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) { message.error('下载失败: ' + (e.message || '网络错误')); }
  };

  const handleGenerateWord = async () => {
    setWordGenerating(true);
    try {
      const res = await generateWordFromTask(task.id);
      if (res.success && res.resultFileId) {
        message.success('Word文档已生成，正在下载...');
        // 自动触发下载
        let token = '';
        try { const s = JSON.parse(localStorage.getItem('auth-storage') || '{}'); token = s?.state?.token || ''; } catch {}
        const fetchRes = await fetch(getFileDownloadUrl(res.resultFileId), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (fetchRes.ok) {
          const blob = await fetchRes.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a'); a.href = url; a.download = res.resultFileName || 'result.doc'; a.click();
          URL.revokeObjectURL(url);
        }
      } else {
        message.error(res.error || '生成失败');
      }
    } catch (e: any) { message.error('生成Word失败: ' + (e.message || '网络错误')); }
    finally { setWordGenerating(false); }
  };

  const formatTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`; if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`; return `${(ms / 60000).toFixed(1)}min`;
  };

  const Avatar = () => (
    <div className="tc-avatar">
      AI
    </div>
  );

  const renderDocList = (step: TaskProgress) => {
    if (!step.docs || step.docs.length === 0) return null;
    return (
      <div style={{ marginTop: 4, marginLeft: 17, maxHeight: 150, overflowY: 'auto' }}>
        {step.docs.map((doc, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0', borderBottom: i < step.docs!.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
            <Text style={{ fontSize: 12, color: '#555', flex: 1 }} ellipsis={{ tooltip: doc.name }}>{i + 1}. {doc.name}</Text>
            <Tag color={doc.score > 0.3 ? 'green' : doc.score > 0.15 ? 'blue' : 'default'} style={{ fontSize: 11, marginLeft: 4, lineHeight: '18px', padding: '0 6px' }}>{(doc.score * 100).toFixed(0)}%</Tag>
          </div>
        ))}
      </div>
    );
  };

  const renderExpList = (step: TaskProgress) => {
    if (!step.experience || step.experience.length === 0) return null;
    return (
      <div style={{ marginTop: 4, marginLeft: 17, maxHeight: 150, overflowY: 'auto' }}>
        {step.experience.map((exp, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0', borderBottom: i < step.experience!.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ fontSize: 11, color: '#722ed1', fontWeight: 500 }}>🧠 经验</Text>
              <Text style={{ fontSize: 12, color: '#888' }} ellipsis={{ tooltip: exp.question }}>{exp.question}</Text>
              <Text style={{ fontSize: 11, color: '#aaa' }} ellipsis={{ tooltip: exp.summary }}>{exp.summary?.slice(0, 60)}</Text>
            </div>
            <Tag color="purple" style={{ fontSize: 11, marginLeft: 4, lineHeight: '18px', padding: '0 6px' }}>{(exp.score * 100).toFixed(0)}%</Tag>
          </div>
        ))}
      </div>
    );
  };

  const Bubble = () => {
    const borderColor = task.status === 'running' ? '#1677ff' : task.status === 'completed' ? '#52c41a' : task.status === 'failed' ? '#ff4d4f' : '#d9d9d9';
    const displayCommand = trimCommand(task.command);

    return (
      <div className="tc-task-card">
        {/* 头部 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Space size={8}>
            <Text strong style={{ fontSize: 15, color: '#1677ff' }}>我</Text>
            <Tag color={cfg.color} icon={cfg.icon} style={{ fontSize: 12, margin: 0, lineHeight: '20px', padding: '0 6px', borderRadius: 6 }}>{cfg.label}</Tag>
            {isFileResult && <Tag color={task.resultType === 'pdf' ? 'red' : 'blue'} style={{ fontSize: 12, margin: 0, lineHeight: '20px', padding: '0 6px', borderRadius: 6 }}>{task.resultType === 'word' ? <><FileWordOutlined /> Word</> : task.resultType === 'pdf' ? <><FilePdfOutlined /> PDF</> : <><FileExcelOutlined /> Excel</>}</Tag>}
            {task.attachedFileName && <Tag color="purple" style={{ fontSize: 12, margin: 0, lineHeight: '20px', padding: '0 6px', borderRadius: 6 }}><FileOutlined style={{ marginRight: 2 }} />{task.attachedFileName.length > 15 ? task.attachedFileName.slice(0, 12) + '...' : task.attachedFileName}</Tag>}
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>{new Date(task.createdAt).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</Text>
        </div>

        {/* 指令 */}
        <div className="tc-command-box" style={{ padding: '10px 14px', marginBottom: 8 }}>
          <Text style={{ fontSize: 14, color: '#333', lineHeight: 1.6 }}>{displayCommand}</Text>
          {task.attachedFileName && <div style={{ marginTop: 6 }}><Text type="secondary" style={{ fontSize: 12 }}>📎 引用文件: <Text strong style={{ fontSize: 12 }}>{task.attachedFileName}</Text></Text></div>}
        </div>

        {/* 执行步骤 */}
        {progressSteps.length > 0 && (
          <div className="tc-progress-box" style={{ padding: '10px 14px', marginBottom: 8 }}>
            <Text style={{ fontSize: 13, color: task.status === 'completed' ? '#52c41a' : '#1677ff', fontWeight: 600, marginBottom: 6, display: 'block' }}>{task.status === 'completed' ? '✅ 执行过程' : '⏳ 执行进度'}</Text>
            {progressSteps.map((step, i) => {
              const isLast = i === progressSteps.length - 1;
              const isDone = task.status === 'completed' || (task.status === 'running' && !isLast);
              const isRag = step.step === 'rag' || step.step === 'web_intel';
              return (
                <div key={i} style={{ padding: '3px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    {isDone ? <CheckCircleFilled style={{ fontSize: 12, color: '#52c41a', marginTop: 2 }} /> : <LoadingOutlined style={{ fontSize: 12, color: '#1677ff', marginTop: 2 }} />}
                    <Text style={{ fontSize: 13, color: isDone ? '#666' : '#1677ff', fontWeight: isDone ? 400 : 500, lineHeight: '18px' }}>{step.detail}</Text>
                  </div>
                  {isRag && renderDocList(step)}
                  {isRag && renderExpList(step)}
                </div>
              );
            })}
          </div>
        )}

        {task.status === 'running' && progressSteps.length === 0 && (
          <div className="tc-running-box" style={{ padding: '10px 14px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <LoadingOutlined style={{ fontSize: 14, color: '#1677ff' }} /><Text style={{ fontSize: 13, color: '#1677ff', fontWeight: 500 }}>正在执行...</Text>
          </div>
        )}

        {/* AI思考过程 — 折叠面板（运行中自动展开实时显示） */}
        {hasThinking && (
          <div style={{ marginBottom: 8 }}>
            <Button
              type="link"
              icon={thinkingExpanded ? <UpOutlined /> : <DownOutlined />}
              onClick={() => setThinkingExpanded(!thinkingExpanded)}
              style={{ padding: '0 4px', fontSize: 13, color: '#722ed1', fontWeight: 500 }}
            >
              <BulbOutlined style={{ marginRight: 4 }} />
              {task.status === 'running'
                ? '💬 AI实时输出中（展开后在框内滚动查看）'
                : thinkingExpanded ? '收起输出过程' : '查看AI输出过程'}
            </Button>
            {thinkingExpanded && (
              <div
                className="tc-thinking-box"
                style={{
                  padding: '12px 16px',
                  marginTop: 6,
                  height: task.status === 'running' ? 260 : undefined,
                  maxHeight: task.status === 'running' ? 260 : 400,
                  overflowY: 'auto',
                }}
              >
                <div className="md-content"><ReactMarkdown remarkPlugins={[remarkGfm]}>{thinkingContent || ''}</ReactMarkdown></div>
              </div>
            )}
          </div>
        )}

        {/* 结果 — 重构为醒目卡片 */}
        {task.status === 'completed' && task.result && (
          <div className="tc-result-box" style={{
            background: 'linear-gradient(145deg, rgba(52,211,153,.08), rgba(52,231,255,.055))',
            borderRadius: 14,
            border: '1px solid rgba(170,205,255,.13)',
            padding: '14px 18px',
            boxShadow: '0 2px 8px rgba(82,196,26,0.08)',
          }}>
            {/* 结果标题 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <CheckCircleFilled style={{ fontSize: 14, color: '#52c41a' }} />
              <Text strong style={{ fontSize: 14, color: '#135200' }}>AI 执行结果</Text>
              {task.totalTokens > 0 && <Text type="secondary" style={{ fontSize: 12 }}>{(task.totalTokens / 1000).toFixed(1)}K tokens</Text>}
              {task.elapsedMs > 0 && <Text type="secondary" style={{ fontSize: 12 }}>⏱ {formatTime(task.elapsedMs)}</Text>}
              <div style={{ flex: 1 }} />
              {!isFileResult && (
                <Button size="small" type="primary" ghost icon={<FileWordOutlined />} onClick={handleGenerateWord} loading={wordGenerating}>
                  导出Word
                </Button>
              )}
            </div>

            {/* 结果内容（非文件类型） */}
            {!isFileResult && (
              <div style={{ fontSize: 14, color: 'var(--df-text-2)', lineHeight: 1.8, maxHeight: expanded ? 'none' : 300, overflow: 'hidden', position: 'relative' }}>
                <div className="md-content"><ReactMarkdown remarkPlugins={[remarkGfm]}>{task.result || ''}</ReactMarkdown></div>
              </div>
            )}
            {!isFileResult && (task.result || '').length > 300 && !expanded && (
              <div style={{ position: 'absolute', bottom: -2, left: 0, right: 0, height: 40, background: 'linear-gradient(transparent, rgba(255,255,255,0.95))' }} />
            )}

            {/* 文件结果 */}
            {isFileResult && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                background: '#fafafa', borderRadius: 8, padding: '12px 16px', marginTop: 6,
              }}>
                {task.resultType === 'word'
                  ? <FileWordOutlined style={{ fontSize: 32, color: '#2b579a' }} />
                  : task.resultType === 'pdf'
                    ? <FilePdfOutlined style={{ fontSize: 32, color: '#cf1322' }} />
                    : <FileExcelOutlined style={{ fontSize: 32, color: '#217346' }} />
                }
                <div style={{ flex: 1 }}>
                  <Text strong style={{ fontSize: 14, color: '#333', display: 'block' }}>
                    {task.resultFileName || (task.resultType === 'word' ? 'Word文档' : task.resultType === 'pdf' ? 'PDF文档' : 'Excel表格')}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>已生成完成，可预览或下载</Text>
                </div>
                <Space>
                  <Button size="middle" type="primary" icon={<EyeOutlined />} onClick={handlePreview}>预览</Button>
                  <Button size="middle" icon={<DownloadOutlined />} onClick={handleDownload}>下载</Button>
                </Space>
              </div>
            )}
          </div>
        )}

        {task.status === 'failed' && task.result && (
          <div style={{ background: 'linear-gradient(135deg, #fff2f0 0%, #fff1f0 100%)', borderRadius: 14, border: '1px solid #ffccc7', padding: '14px 18px' }}>
            <Text style={{ fontSize: 14, color: '#ff4d4f', lineHeight: 1.6 }}>{task.result}</Text>
          </div>
        )}

        {/* 底部操作栏 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
          <Space size={8}>
            {task.totalTokens > 0 && <Text type="secondary" style={{ fontSize: 12 }}>🎯 {(task.totalTokens / 1000).toFixed(1)}K tokens</Text>}
            {task.elapsedMs > 0 && <Text type="secondary" style={{ fontSize: 12 }}>⏱ {formatTime(task.elapsedMs)}</Text>}
          </Space>
          <Space size={6}>
            {/* 我来试试 */}
            <Tooltip title={task.attachedFileName ? `复制指令和文件引用: ${task.attachedFileName}` : '复制指令到输入框'}>
              <Button
                size="middle"
                type="primary"
                ghost
                icon={<ThunderboltOutlined />}
                onClick={() => onTryIt?.(task)}
                style={{ fontSize: 13, borderRadius: 8, fontWeight: 500, height: 34 }}
              >
                我来试试
              </Button>
            </Tooltip>
            {isFileResult && task.resultFileId && task.status === 'completed' && (
              <Button size="middle" type="link" icon={<DownloadOutlined />} onClick={handleDownload} style={{ fontSize: 13 }}>
                下载
              </Button>
            )}
            {!isFileResult && task.result && task.result.length > 300 && (
              <Button size="middle" type="link" onClick={() => setExpanded(!expanded)} style={{ fontSize: 13 }}>
                {expanded ? '收起' : '展开全部'}
              </Button>
            )}
            {/* 生成Word — 文本结果可转为Word文档 */}
            {!isFileResult && task.status === 'completed' && task.result && (
              <Button
                size="middle"
                type="link"
                icon={<FileWordOutlined />}
                onClick={handleGenerateWord}
                loading={wordGenerating}
                style={{ fontSize: 13 }}
              >
                生成Word
              </Button>
            )}
          </Space>
        </div>
      </div>
    );
  };

  const renderPreviewContent = () => {
    if (previewLoading) return <div style={{ textAlign: 'center', padding: 40 }}><LoadingOutlined style={{ fontSize: 24 }} /></div>;
    if (!previewData) return null;
    if (previewData.error) return <Text type="danger">{previewData.error}</Text>;
    if (previewData.type === 'csv' && previewData.headers && previewData.rows) {
      const columns = previewData.headers.map((h, i) => ({ title: h, dataIndex: `col${i}`, key: `col${i}`, ellipsis: true, width: 120 }));
      const dataSource = previewData.rows.map((row, i) => { const obj: any = { key: i }; row.forEach((val, j) => { obj[`col${j}`] = val; }); return obj; });
      return <Table columns={columns} dataSource={dataSource} size="small" scroll={{ x: 'max-content' }} pagination={{ pageSize: 50, size: 'small' }} />;
    }
    if (task.resultType === 'pdf') return <div style={{ fontSize: 14, padding: '18px', background: '#fff7f7', border: '1px solid #ffccc7', borderRadius: 8, color: '#a8071a' }}>PDF文档已生成，请点击下方“下载”查看正式文件。</div>;
    if (previewData.type === 'doc' && previewData.content) return <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, maxHeight: '60vh', overflow: 'auto', padding: '12px', background: '#fafafa', borderRadius: 8, lineHeight: 1.8 }}>{previewData.content}</div>;
    return <Text type="secondary">暂不支持此文件类型的预览</Text>;
  };

  return (
    <div style={{ display: 'flex', justifyContent: isOwn ? 'flex-end' : 'flex-start', alignItems: 'flex-start', gap: 10, marginBottom: 16, padding: '0 8px' }}>
      {!isOwn && <Avatar />}<Bubble />{isOwn && <Avatar />}
      <Modal title={`📄 ${task.resultFileName || '文件预览'}`} open={previewVisible} onCancel={() => setPreviewVisible(false)}
        footer={[<Button key="download" icon={<DownloadOutlined />} onClick={handleDownload} size="middle">下载</Button>, <Button key="close" onClick={() => setPreviewVisible(false)} size="middle">关闭</Button>]}
        width={960} style={{ top: 20 }}>
        {renderPreviewContent()}
      </Modal>
    </div>
  );
};

export default TaskCard;
